/**
 * The gateway's `GET /api/version`, read in ONE place.
 *
 * The gateway answers through the shared API envelope — `{ok:true,data:{…}}` —
 * so the version lives at `data.version`, never at the top level. Three call
 * sites previously read `body.version` behind an `as { version?: string }` cast:
 * the cast asserted a shape the response does not have, so the compiler stayed
 * quiet and every read returned undefined on every install. Centralising the
 * parse is what stops that from being re-derived independently a fourth time.
 *
 * The outcome is a discriminated union rather than `string | undefined` because
 * each caller renders a genuinely different thing: a status command distinguishes
 * "auth-gated" from "unknown", a converge narrator warns differently for an HTTP
 * error than for an unreachable host, and a health probe treats reachability and
 * version as separate questions. Collapsing them would push guesswork back out
 * to the callers.
 */
export type GatewayVersionRead =
  /** The gateway answered and named its version. */
  | { kind: "version"; version: string }
  /** The gateway answered 2xx, but no version could be read from the envelope. */
  | { kind: "no-version" }
  /** 401/403 — the gateway is up, the endpoint is gated. */
  | { kind: "auth-gated"; status: number }
  /** Any other non-2xx. */
  | { kind: "http-error"; status: number }
  /** The request never completed (DNS, TLS, connection refused, …). */
  | { kind: "unreachable"; detail: string };

/**
 * Pull the version out of the API envelope. Narrows from `unknown` — no cast —
 * and deliberately does NOT fall back to a top-level `version`: that is the
 * shape the old bug assumed, and accepting it would make a regression to it
 * invisible. An `ok:false` envelope carries `error` rather than `data`, so it
 * lands on "no-version" without needing a separate check.
 */
function versionFromEnvelope(body: unknown): string | undefined {
  if (typeof body !== "object" || body === null) return undefined;
  const data = (body as { data?: unknown }).data;
  if (typeof data !== "object" || data === null) return undefined;
  const version = (data as { version?: unknown }).version;
  return typeof version === "string" && version.length > 0 ? version : undefined;
}

/**
 * The read as a diagnostic surface prints it (`m8t platform status --verify`).
 *
 * "unknown" and "unknown (auth-gated)" are different claims and must stay so:
 * the first says the version could not be established, the second says the
 * gateway is up and declined to say. Collapsing them would hide a working
 * gateway behind the same word used for a broken one.
 */
export function renderVersionRead(read: GatewayVersionRead): string {
  if (read.kind === "version") return read.version;
  if (read.kind === "auth-gated") return "unknown (auth-gated)";
  return "unknown";
}

export async function readGatewayVersion(
  gatewayUrl: string,
  fetchImpl: typeof fetch = fetch,
): Promise<GatewayVersionRead> {
  let res: Response;
  try {
    res = await fetchImpl(`${gatewayUrl}/api/version`, { method: "GET" });
  } catch (e) {
    return { kind: "unreachable", detail: (e as Error).message };
  }
  if (res.status === 401 || res.status === 403) return { kind: "auth-gated", status: res.status };
  if (!res.ok) return { kind: "http-error", status: res.status };
  const body: unknown = await res.json().catch(() => undefined);
  const version = versionFromEnvelope(body);
  return version === undefined ? { kind: "no-version" } : { kind: "version", version };
}
